// Imports
import React, { useRef, useState, useContext, useEffect } from 'react';
import uniqueId from 'lodash/uniqueId';
import { ThemeProvider } from 'styled-components/macro';
import useUpdateOnlyEffect from '../../../../../../shareables/foundation/front-end/utils/use-update-only-effect';
import FieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import { Label } from '../../../../../../shareables/foundation/front-end/components/forms/labels';
import FieldErrorMessage from '../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import {
	FileButton,
	FileButtonIcon,
	FilesDescriptor,
	FileWrapper,
	FileContainer,
} from '../../../../../../shareables/foundation/front-end/components/forms/file-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpFromLine, faImage, faFilePdf } from '@fortawesome/pro-regular-svg-icons';
import APIConnectedFormContext, { ContextDetails } from '../../../../../contexts/api-connected-form';
import BaseFieldProps from './base-field-props';


// Define the accepted props
type ExtraProps = BaseFieldProps &
	Omit<
		React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
		| 'id'
		| 'className'
		| 'value'
		| 'defaultValue'
		| 'type'
		| 'checked'
		| 'defaultChecked'
		| 'prefix'
		| 'placeholder'
		| 'onChange'
		| 'multiple'
	>;

interface PropsBase extends ExtraProps {
	/** A custom phrase to display inside the field. If not provided, an appropriate default is chosen based on the accepted file types. */
	prompt?: string;
	
	
	/** Simulates a file being dragged over the field in Storybook. */
	storybookDragHovering?: true;
	
	
	/** Simulates selected filenames in Storybook. */
	storybookFilenames?: string[];
}

export interface SingleFileProps extends PropsBase {
	/** Whether the field should allow selecting multiple files. Defaults to `false`. */
	multiple?: false;
	
	
	/** A function that handles a change in value to the field. The function is passed the latest file as its parameter. */
	onChange?: (newFiles: File | null) => void;
	
	
	/** A function that provides the getter for the field to be bound, usually to a `ref`. */
	bindGetter?: (getter: () => File | null) => void;
	
	
	/** A function that provides the setter for the field to be bound, usually to a `ref`. */
	bindSetter?: (setter: (files: File | null) => void) => void;
}

export interface MultipleFileProps extends PropsBase {
	/** Whether the field should allow selecting multiple files. Defaults to `false`. */
	multiple: true;
	
	
	/** A function that handles a change in value to the field. The function is passed the latest files as its parameter. */
	onChange?: (newFiles: FileList | null) => void;
	
	
	/** A function that provides the getter for the field to be bound, usually to a `ref`. */
	bindGetter?: (getter: () => FileList | null) => void;
	
	
	/** A function that provides the setter for the field to be bound, usually to a `ref`. */
	bindSetter?: (setter: (files: FileList | null) => void) => void;
}

type CompleteProps = SingleFileProps | MultipleFileProps;


// Function component
const File = React.forwardRef<HTMLInputElement, CompleteProps>((props: SingleFileProps | MultipleFileProps, ref) => {
	// Break off the props we don't want to give to the intrinsic element
	const {
		label,
		note,
		prompt,
		errorMessage: errorMessageProp,
		onChange,
		bindGetter,
		bindSetter,
		multiple,
		exclude: excludeProp,
		storybookDragHovering,
		storybookFilenames,
		...passThroughProps
	} = props;
	
	
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Retrieve errors from API form's context, falling back to props
	let errorMessage = context?.error.field === passThroughProps.name ? context.error.message : errorMessageProp;
	
	
	// If the multiple prop was passed, capture errors that were thrown for individual files
	if (multiple) {
		// Capture the error field before the last set of square brackets
		const baseErrorField = context?.error.field?.match(/(.+)\[[0-9]+\]$/)?.[1];
		
		
		// Check for a match with the field name
		if (baseErrorField === passThroughProps.name) {
			errorMessage = context?.error.message;
		}
	}
	
	
	// Decide if the field is excluded
	const exclude = excludeProp ?? passThroughProps.disabled ?? undefined;
	
	
	// Use state
	const [files, setFiles] = useState<FileList | null>(null);
	const [file, setFile] = useState<File | null>(null);
	const [fileDragHovering, setFileDragHovering] = useState(storybookDragHovering ?? false);
	
	
	// Bind getter/setter
	if (multiple) {
		bindGetter?.(() => files);
		bindSetter?.(setFiles);
	} else {
		bindGetter?.(() => file);
		bindSetter?.(setFile);
	}
	
	
	// Extract name prop for use in effects
	const { name } = passThroughProps;
	
	
	// Notify context that this component is the container for individual file errors
	let addErrorContainer: NonNullable<ContextDetails>['addErrorContainer'] | null = null;
	let removeErrorContainer: NonNullable<ContextDetails>['removeErrorContainer'] | null = null;
	
	if (context) {
		addErrorContainer = context.addErrorContainer;
		removeErrorContainer = context.removeErrorContainer;
	}
	
	useEffect(() => {
		// Return unless multiple files are supported and the required details are defined
		if (!addErrorContainer || !multiple || !files) {
			return;
		}
		
		
		// Define error container names; the browser's file list is live, and can empty before the cleanup runs
		const errorContainerNames = Array.from({ length: files.length }, (_ignore, index) => `${name}[${index}]`);
		
		
		// Add per-file error containers
		for (const errorContainerName of errorContainerNames) {
			addErrorContainer(errorContainerName);
		}
		
		
		// Return cleanup function
		return () => {
			// Return if error container remover is not available
			if (!removeErrorContainer) {
				return;
			}
			
			
			// Remove each error container using the previously defined names
			for (const errorContainerName of errorContainerNames) {
				removeErrorContainer(errorContainerName);
			}
		};
	}, [files, addErrorContainer, removeErrorContainer, name, multiple]);
	
	
	// Add and remove getter from connected form
	let addFileGetter: NonNullable<ContextDetails>['addFileGetter'] | null = null;
	let removeFileGetter: NonNullable<ContextDetails>['removeFileGetter'] | null = null;
	
	if (context) {
		addFileGetter = context.addFileGetter;
		removeFileGetter = context.removeFileGetter;
	}
	
	useEffect(() => {
		if (addFileGetter === null || exclude === true) {
			return;
		}
		
		addFileGetter(name, () => (multiple ? files : file));
		
		return () => {
			if (removeFileGetter === null) {
				return;
			}
			
			removeFileGetter(name);
		};
	}, [name, files, addFileGetter, removeFileGetter, exclude, file, multiple]);
	
	
	// Generate unique ID
	const uniqueID = useRef(uniqueId('file-field-'));
	
	
	// Handle value changes
	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
		if (multiple) {
			setFiles(event.target.files);
		} else {
			setFile(event.target.files?.[0] || null);
		}
	};
	
	
	// Notify parent of changes
	useUpdateOnlyEffect(() => {
		if (multiple) {
			onChange?.(files);
		} else {
			onChange?.(file);
		}
		
		context?.onChange?.();
	}, [files, file, multiple]);
	
	useUpdateOnlyEffect(() => {
		context?.onExcludeChange?.();
	}, [exclude]);
	
	
	// Special handling for file inputs
	let fileIcon = faUpFromLine;
	let filePrompt = 'Choose a file…';
	const filenames: string[] = storybookFilenames ?? [];
	
	
	// Add some additional props
	passThroughProps.onDragEnter = () => {
		setFileDragHovering(true);
	};
	
	passThroughProps.onDragLeave = () => {
		setFileDragHovering(false);
	};
	
	passThroughProps.onDrop = () => {
		setFileDragHovering(false);
	};
	
	
	// Customize prompt
	if (multiple) {
		filePrompt = 'Choose files…';
		
		if (passThroughProps.accept) {
			if (passThroughProps.accept.includes('image/')) {
				fileIcon = faImage;
				filePrompt = 'Choose images…';
			} else if (passThroughProps.accept.includes('/pdf')) {
				fileIcon = faFilePdf;
				filePrompt = 'Choose PDFs…';
			} else if (passThroughProps.accept.includes('/csv')) {
				filePrompt = 'Choose CSV files…';
			}
		}
	} else {
		if (passThroughProps.accept) {
			if (passThroughProps.accept.includes('image/')) {
				fileIcon = faImage;
				filePrompt = 'Choose an image…';
			} else if (passThroughProps.accept.includes('/pdf')) {
				fileIcon = faFilePdf;
				filePrompt = 'Choose a PDF…';
			} else if (passThroughProps.accept.includes('/csv')) {
				filePrompt = 'Choose a CSV file…';
			}
		}
	}
	
	if (prompt) {
		filePrompt = prompt;
	}
	
	
	// Determine filename(s) to display
	if (file) {
		filenames.push(file.name);
	}
	
	if (files) {
		for (const file of files) {
			filenames.push(file.name);
		}
	}
	
	
	// Create theme
	const theme = {
		hasError: Boolean(errorMessage),
	};
	
	
	// Return JSX
	return (
		<ThemeProvider theme={theme}>
			<FieldContainer>
				{label && (
					<Label htmlFor={uniqueID.current}>
						{label}
						{note && <small>{note}</small>}
					</Label>
				)}
				
				<FileWrapper>
					<FileContainer>
						<input
							{...passThroughProps}
							type='file'
							id={uniqueID.current}
							onChange={handleChange}
							multiple={multiple}
							aria-invalid={Boolean(errorMessage)}
							aria-describedby={`error-field-${uniqueID.current}`}
							ref={ref}
						/>
						
						<FileButton dragHovering={fileDragHovering}>
							<FileButtonIcon>
								<FontAwesomeIcon icon={fileIcon} />
							</FileButtonIcon>
							
							{filePrompt}
						</FileButton>
						
						{Boolean(filenames.length) && (
							<FilesDescriptor>
								{filenames.length > 3 ? `${filenames.length} files selected` : filenames.join(', ')}
							</FilesDescriptor>
						)}
					</FileContainer>
				</FileWrapper>
				
				{errorMessage && <FieldErrorMessage id={`error-field-${uniqueID.current}`} message={errorMessage} />}
			</FieldContainer>
		</ThemeProvider>
	);
});


// @ts-ignore: displayName does exist
File.displayName = 'File';

export default File;
